import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { IoMdClose } from 'react-icons/io';
import { RiArrowRightSLine, RiThumbUpLine, RiThumbDownLine } from 'react-icons/ri';
import { LazyLoadImage } from 'react-lazy-load-image-component';
import { useTranslation } from 'react-i18next';

import showNotification from 'utils/showNotification';
import { handleTrackEvent } from 'utils/analytics';
import { useEvieAppContext } from '../EvieAppProvider';
import { useMarkContentAsCompleted } from '../Service/useMarkContentAsCompleted';

import eviePoints from '../../../assets/images/evi-points-big.webp';

import './readingView.css';
import 'react-lazy-load-image-component/src/effects/blur.css';

const ReadingView = ({ isAssistantView, changeViewStatus, selectContent, selectedContent }) => {
  const { t, i18n } = useTranslation();
  const currentLang = i18n.language.split('-')[0];
  const { refetch, activeProgram = {} } = isAssistantView ? {} : useEvieAppContext();
  const { markContentCompleted } = useMarkContentAsCompleted();
  const [hasTriggeredScroll, setHasTriggeredScroll] = useState(false);

  const completedActivities =
    (activeProgram &&
      activeProgram.currentLevelCompletedActivities &&
      activeProgram.currentLevelCompletedActivities[0] &&
      activeProgram.currentLevelCompletedActivities[0].activities) ||
    [];
  const patientProgramId = activeProgram && activeProgram.id;

  const {
    backgroundImage,
    backgroundImageUrl,
    title,
    duration,
    points = 10,
    contentJson,
    id: contentId,
    contentType,
    __typename,
  } = selectedContent;
  const currentContentType = contentType || __typename; // contentType is used for chat recommended content, __typename is used for daily exercises task
  const currentBackgroundImage = (backgroundImage && backgroundImage.original) || backgroundImageUrl;
  const getLocalizedContent = (contentObj) =>
    (contentObj && contentObj[currentLang]) || (contentObj && contentObj.en) || '';

  const handleScroll = async ({ target }) => {
    const { scrollHeight, scrollTop, clientHeight } = target;
    const hasReachedBottom = Math.abs(scrollHeight - scrollTop - clientHeight) <= 20;
    if (!hasReachedBottom || hasTriggeredScroll) return;

    // Chat recommendations are tracked but never marked as program progress.
    if (isAssistantView) {
      setHasTriggeredScroll(true);
      handleTrackEvent('Recommendation completed', { type: currentContentType, id: contentId, title });
      return;
    }

    const isAlreadyCompleted = completedActivities.some((activity) => activity.id === contentId);
    if (isAlreadyCompleted) return;

    setHasTriggeredScroll(true);

    try {
      const result = await markContentCompleted({
        contentId,
        contentType: currentContentType.toLowerCase(),
        patientProgramId,
        sessionDuration: String(duration),
      });

      if (result.success) {
        await refetch();
        changeViewStatus();
        selectContent(null);
        showNotification({
          type: 'success',
          messages: [t('evieApp.ReadingView.completedSuccess', 'Reading completed successfully!')],
        });
      } else {
        showNotification({
          type: 'error',
          messages: [result.error || t('evieApp.ReadingView.completedError', 'Error completing reading')],
        });
      }
    } catch (error) {
      console.error('Error en la mutación:', error);
      showNotification({
        type: 'error',
        messages: [t('evieApp.ReadingView.completedError', 'Error completing reading')],
      });
    }
  };

  useEffect(() => {
    window.scrollTo(0, 0);
    const readingViewElement = document.querySelector('.reading-view');
    if (readingViewElement) {
      readingViewElement.scrollTo(0, 0);
    }
  }, []);

  const handleClose = () => {
    changeViewStatus();
    selectContent(null);
  };

  const renderContent = {
    text: (item) => <p key={item.id}>{getLocalizedContent(item && item.content)}</p>,
    divider: (item) => <div className="divider" key={item.id} />,
    bullet_list: (item) => (
      <div className="bullet-list" key={item.id}>
        {item.items &&
          item.items.map((bullet, index) => (
            <div className="bullet-item" key={index}>
              <RiArrowRightSLine />
              <span>{getLocalizedContent(bullet)}</span>
            </div>
          ))}
      </div>
    ),
    heading_1: (item) => (
      <p className="heading heading-1" key={item.id}>
        {getLocalizedContent(item && item.content)}
      </p>
    ),
    heading_2: (item) => (
      <p className="heading heading-2" key={item.id}>
        {getLocalizedContent(item && item.content)}
      </p>
    ),
    heading_3: (item) => (
      <p className="heading heading-3" key={item.id}>
        {getLocalizedContent(item && item.content)}
      </p>
    ),
    image: (item) => (
      <div className="article-image" key={item.id}>
        <LazyLoadImage src={item.url} alt="" effect="blur" width="100%" />
      </div>
    ),
  };

  return (
    <div className="reading-view">
      <div className="reading-header">
        <button type="button" className="close-btn" onClick={handleClose}>
          <IoMdClose />
        </button>
      </div>

      <div className="reading-card" onScroll={handleScroll}>
        {currentBackgroundImage && (
          <div className="main-image">
            <img src={currentBackgroundImage} alt={title || ''} />
          </div>
        )}

        <div className="reading-body">
          <h3 className="body-title">{title}</h3>
          <span className="team-label">{t('createTemplate.readings.team')}</span>

          <div className="metadata">
            <span className="duration">
              {duration} {duration === 1 ? t('minute', 'minute') : t('minutes', 'minutes')}
            </span>
            <div className="points">
              <span>+{points}</span>
              <img src={eviePoints} alt="Evie Points" />
            </div>
          </div>

          <div className="article-content">
            {contentJson &&
              contentJson.map((item) => {
                const itemType = item && item.type;
                const renderFn = itemType && renderContent[itemType];
                return renderFn ? renderFn(item) : null;
              })}
          </div>

          <div className="feedback-section">
            <p>{t('createTemplate.readings.feedback')}</p>
            <div className="feedback-buttons">
              <RiThumbUpLine />
              <RiThumbDownLine />
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

ReadingView.defaultProps = {
  isAssistantView: false,
};

ReadingView.propTypes = {
  isAssistantView: PropTypes.bool,
  changeViewStatus: PropTypes.func.isRequired,
  selectContent: PropTypes.func.isRequired,
  selectedContent: PropTypes.shape({
    __typename: PropTypes.string,
    contentType: PropTypes.string,
    id: PropTypes.string,
    title: PropTypes.string,
    duration: PropTypes.number,
    points: PropTypes.number,
    backgroundImage: PropTypes.string,
    backgroundImageUrl: PropTypes.string,
    contentJson: PropTypes.arrayOf(PropTypes.shape({ id: PropTypes.string })).isRequired,
  }).isRequired,
};

export default ReadingView;
